import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  FileText,
  PlusCircle,
  RefreshCcw,
  Trash2,
  Eye,
  Paperclip,
  Send,
  CheckCircle,
  XCircle,
  Route,
  AlertTriangle,
  Ban,
} from "lucide-react";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import {
  canConvertProformaToFacture,
  canCreatePassageFromProforma,
  canSendProforma,
  canValidateProformaClient,
} from "../../utils/roleAccess";

const TERRAIN_TYPES = new Set([
  "GROS_TRAVAUX",
  "CHANTIER",
  "URGENCE_PAYANTE",
  "NETTOYAGE_EXCEPTIONNEL",
  "COLLECTE_SPECIALE",
  "HORS_ABONNEMENT",
]);

interface PassageLite {
  id: number;
  statut: string;
  isFacturable: boolean;
  datePrevue?: string;
}

interface FactureLite {
  id: number;
  numero: string;
  montantTTC: number;
  statut: string;
}

interface ClientLite {
  id: number;
  nom?: string | null;
  prenom?: string | null;
  nomEntreprise?: string | null;
}

interface MissionLite {
  id: number;
  numero: string;
  statut: string;
}

interface ProformaRow {
  id: number;
  numero: string;
  client: ClientLite;
  mission?: MissionLite | null;
  montantHT: number;
  tva: number;
  montantTTC: number;
  statut: string;
  cycleStatut?: string;
  typeMetier: string | null;
  devise: string;
  fichierPDF?: string;
  attachment?: string | null;
  facture?: FactureLite | null;
  passage?: PassageLite | null;
  dateEmission: string;
}

function clientLabel(c: ClientLite): string {
  return (
    c.nomEntreprise ||
    `${c.prenom ?? ""} ${c.nom ?? ""}`.trim() ||
    "—"
  );
}

function isTerrainType(typeMetier: string | null | undefined): boolean {
  return !!typeMetier && TERRAIN_TYPES.has(typeMetier);
}

/** Aligné sur les règles backend : conversion autorisée uniquement si eligible. */
function conversionBlockedReason(p: ProformaRow): string | null {
  if (p.facture || p.cycleStatut === "FACTUREE") return null;
  if (p.passage?.id) {
    if (!p.passage.isFacturable) {
      return "Passage non validé : facturation impossible.";
    }
    if (p.cycleStatut !== "PASSAGE_CREE") {
      return "Cycle incompatible pour facturation terrain.";
    }
    return null;
  }
  if (isTerrainType(p.typeMetier)) {
    return "Travail terrain : créez et validez un passage avant facturation.";
  }
  if (p.cycleStatut !== "VALIDEE_CLIENT") {
    return "Validation client requise avant conversion.";
  }
  return null;
}

export default function ListeProformas(): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proformas, setProformas] = useState<ProformaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cycleFilter, setCycleFilter] = useState("ALL");
  const [deviseFilter, setDeviseFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [validateOpen, setValidateOpen] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [activeProforma, setActiveProforma] = useState<ProformaRow | null>(null);
  const [validationPar, setValidationPar] = useState("");
  const [motifRefus, setMotifRefus] = useState("");

  const roleSend = canSendProforma(user?.role);
  const roleValidate = canValidateProformaClient(user?.role);
  const rolePassage = canCreatePassageFromProforma(user?.role);
  const roleConvert = canConvertProformaToFacture(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const pRes = await api.get("/proformas", { params: { limit: 100, page: 1 } });
      const proformasData = Array.isArray(pRes.data)
        ? pRes.data
        : Array.isArray(pRes.data?.data)
          ? pRes.data.data
          : [];
      setProformas(proformasData as ProformaRow[]);
    } catch {
      toast.error("Erreur lors du chargement des proformas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    if (!Array.isArray(proformas)) return [];
    const q = search.toLowerCase();
    return proformas.filter((p) => {
      const numero = p.numero?.toLowerCase() ?? "";
      const clientLower = clientLabel(p.client).toLowerCase();
      const matchSearch =
        !q || numero.includes(q) || clientLower.includes(q);
      const cycle = p.cycleStatut ?? "";
      const matchCycle =
        cycleFilter === "ALL" || cycle === cycleFilter;
      const matchDevise =
        deviseFilter === "ALL" || p.devise === deviseFilter;
      const matchFrom =
        !dateFrom ||
        dayjs(p.dateEmission).isAfter(dayjs(dateFrom).subtract(1, "day"));
      const matchTo =
        !dateTo ||
        dayjs(p.dateEmission).isBefore(dayjs(dateTo).add(1, "day"));
      return matchSearch && matchCycle && matchDevise && matchFrom && matchTo;
    });
  }, [proformas, search, cycleFilter, deviseFilter, dateFrom, dateTo]);

  const openPdf = (fileName?: string) => {
    if (!fileName) return toast.warning("Aucun PDF associé.");
    window.open(
      `${import.meta.env.VITE_API_URL}/uploads/proformas/${fileName}`,
      "_blank"
    );
  };

  const handleEnvoyer = async (p: ProformaRow) => {
    try {
      await api.post(`/proformas/${p.id}/envoyer`);
      toast.success(`Proforma ${p.numero} marquée comme envoyée.`);
      fetchData();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Erreur envoi.");
    }
  };

  const handleValidateClient = async () => {
    if (!activeProforma || !validationPar.trim()) {
      toast.warning("Indiquez qui valide (nom ou référence client).");
      return;
    }
    try {
      await api.post(`/proformas/${activeProforma.id}/validate-client`, {
        validationClientPar: validationPar.trim(),
      });
      toast.success("Proforma validée côté client.");
      setValidateOpen(false);
      setActiveProforma(null);
      setValidationPar("");
      fetchData();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Erreur validation.");
    }
  };

  const handleRefuseClient = async () => {
    if (!activeProforma || !motifRefus.trim()) {
      toast.warning("Motif de refus obligatoire.");
      return;
    }
    try {
      await api.post(`/proformas/${activeProforma.id}/refuse-client`, {
        motif: motifRefus.trim(),
      });
      toast.info("Proforma refusée par le client.");
      setRefuseOpen(false);
      setActiveProforma(null);
      setMotifRefus("");
      fetchData();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Erreur refus.");
    }
  };

  const handleCreatePassage = async (p: ProformaRow) => {
    try {
      await api.post(`/proformas/${p.id}/create-passage`);
      toast.success("Passage exceptionnel créé.");
      fetchData();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? "Erreur création passage.");
    }
  };

  const handleConvert = async (p: ProformaRow) => {
    const block = conversionBlockedReason(p);
    if (block) {
      toast.warning(block);
      return;
    }
    try {
      await api.post(`/proformas/${p.id}/convert-to-facture`);
      toast.success("Facture créée.");
      fetchData();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; code?: string } } };
      toast.error(
        err.response?.data?.message ?? "Erreur conversion en facture."
      );
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Supprimer définitivement cette proforma ?")) return;
    try {
      await api.delete(`/proformas/${id}`);
      toast.info("Proforma supprimée.");
      setProformas((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6 text-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fetchData()}
            className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm hover:bg-slate-700"
          >
            <RefreshCcw className="h-4 w-4" /> Actualiser
          </button>
          <Link
            to="/admin/finance/factures"
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm hover:bg-indigo-500"
          >
            <FileText className="h-4 w-4" /> Factures
          </Link>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/finance/proformas/nouvelle")}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500"
        >
          <PlusCircle className="h-4 w-4" /> Nouvelle proforma
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-700 bg-slate-800/40 p-4 md:grid-cols-5">
        <input
          type="text"
          placeholder="Rechercher n° ou client…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        />
        <select
          value={cycleFilter}
          onChange={(e) => setCycleFilter(e.target.value)}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        >
          <option value="ALL">Tous cycles métier</option>
          <option value="BROUILLON">BROUILLON</option>
          <option value="ENVOYEE">ENVOYEE</option>
          <option value="VALIDEE_CLIENT">VALIDEE_CLIENT</option>
          <option value="PASSAGE_CREE">PASSAGE_CREE</option>
          <option value="FACTUREE">FACTUREE</option>
          <option value="REFUSEE">REFUSEE</option>
          <option value="EXPIREE">EXPIREE</option>
        </select>
        <select
          value={deviseFilter}
          onChange={(e) => setDeviseFilter(e.target.value)}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        >
          <option value="ALL">Toutes devises</option>
          <option value="USD">USD</option>
          <option value="CDF">CDF</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-slate-500">Aucune proforma.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-700">
          <table className="w-full min-w-[1100px] text-sm">
            <thead className="bg-slate-800/80 text-left text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-3">Numéro</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Type métier</th>
                <th className="px-3 py-3 text-right">Montant TTC</th>
                <th className="px-3 py-3">Cycle métier</th>
                <th className="px-3 py-3">Passage</th>
                <th className="px-3 py-3">Facture</th>
                <th className="px-3 py-3">Émission</th>
                <th className="px-3 py-3">Badges</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-900/30">
              {filtered.map((p, i) => {
                const cycle = p.cycleStatut ?? "";
                const blockMsg = conversionBlockedReason({
                  ...p,
                  cycleStatut: cycle || p.statut,
                });
                const showPassageBtn =
                  rolePassage &&
                  cycle === "VALIDEE_CLIENT" &&
                  isTerrainType(p.typeMetier) &&
                  !p.passage?.id;
                const canConvertNow =
                  roleConvert &&
                  !p.facture &&
                  cycle !== "FACTUREE" &&
                  !blockMsg;

                return (
                  <motion.tr
                    key={p.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="hover:bg-slate-800/40"
                  >
                    <td className="px-3 py-2 font-mono text-emerald-400">
                      {p.numero}
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-2">
                      {clientLabel(p.client)}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {p.typeMetier ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {(p.montantTTC ?? 0).toLocaleString("fr-CD")} {p.devise}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-amber-200">
                        {cycle || p.statut}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {p.passage?.id ? (
                        <Link
                          to={`/admin/passages/historique`}
                          className="inline-flex items-center gap-1 text-sky-400 hover:underline"
                        >
                          <Route className="h-3.5 w-3.5" /> #{p.passage.id}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.facture ? (
                        <Link
                          to="/admin/finance/factures"
                          className="text-emerald-400 hover:underline"
                        >
                          {p.facture.numero}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-400">
                      {p.dateEmission
                        ? dayjs(p.dateEmission).format("DD/MM/YYYY")
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {isTerrainType(p.typeMetier) && (
                          <span className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[0.65rem] text-amber-200">
                            Terrain requis
                          </span>
                        )}
                        {p.passage?.id && !p.passage.isFacturable && (
                          <span className="rounded bg-red-900/40 px-1.5 py-0.5 text-[0.65rem] text-red-200">
                            Passage non validé
                          </span>
                        )}
                        {p.passage?.isFacturable && (
                          <span className="rounded bg-emerald-900/40 px-1.5 py-0.5 text-[0.65rem] text-emerald-200">
                            Facturable
                          </span>
                        )}
                        {cycle === "FACTUREE" && (
                          <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[0.65rem]">
                            Facturée
                          </span>
                        )}
                        {p.typeMetier && p.typeMetier !== "DEVIS_CLIENT" && (
                          <span className="rounded bg-violet-900/40 px-1.5 py-0.5 text-[0.65rem] text-violet-200">
                            Hors abonnement
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <button
                          type="button"
                          title="PDF"
                          onClick={() => openPdf(p.fichierPDF)}
                          className="rounded p-1.5 text-slate-300 hover:bg-slate-800"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {p.attachment && (
                          <a
                            href={`${import.meta.env.VITE_API_URL}/uploads/proformas/attachments/${p.attachment}`}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded p-1.5 text-indigo-400 hover:bg-slate-800"
                          >
                            <Paperclip className="h-4 w-4" />
                          </a>
                        )}
                        {roleSend && cycle === "BROUILLON" && (
                          <button
                            type="button"
                            title="Envoyer"
                            onClick={() => handleEnvoyer(p)}
                            className="rounded p-1.5 text-cyan-400 hover:bg-slate-800"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        )}
                        {roleValidate && cycle === "ENVOYEE" && (
                          <>
                            <button
                              type="button"
                              title="Validée client"
                              onClick={() => {
                                setActiveProforma(p);
                                setValidationPar("");
                                setValidateOpen(true);
                              }}
                              className="rounded p-1.5 text-emerald-400 hover:bg-slate-800"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Refus client"
                              onClick={() => {
                                setActiveProforma(p);
                                setMotifRefus("");
                                setRefuseOpen(true);
                              }}
                              className="rounded p-1.5 text-orange-400 hover:bg-slate-800"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {showPassageBtn && (
                          <button
                            type="button"
                            title="Créer passage exceptionnel"
                            onClick={() => handleCreatePassage(p)}
                            className="rounded p-1.5 text-amber-400 hover:bg-slate-800"
                          >
                            <Route className="h-4 w-4" />
                          </button>
                        )}
                        {roleConvert &&
                          !p.facture &&
                          cycle !== "FACTUREE" && (
                            <button
                              type="button"
                              title={
                                blockMsg ?? "Convertir en facture"
                              }
                              disabled={!canConvertNow}
                              onClick={() => handleConvert(p)}
                              className={`rounded p-1.5 hover:bg-slate-800 ${
                                blockMsg
                                  ? "cursor-not-allowed text-slate-600"
                                  : "text-green-400"
                              }`}
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                        {blockMsg && roleConvert && !p.facture && (
                          <span
                            title={blockMsg}
                            className="inline-flex items-center p-1.5 text-amber-500"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </span>
                        )}
                        {cycle === "BROUILLON" && (
                          <button
                            type="button"
                            title="Supprimer"
                            onClick={() => handleDelete(p.id)}
                            className="rounded p-1.5 text-red-500 hover:bg-slate-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {validateOpen && activeProforma && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-emerald-400">
              Validation client — {activeProforma.numero}
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Référence du signataire ou libellé de validation (audit).
            </p>
            <input
              className="mt-4 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              placeholder="Ex. Percy Tuluka — Bon pour accord"
              value={validationPar}
              onChange={(e) => setValidationPar(e.target.value)}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setValidateOpen(false);
                  setActiveProforma(null);
                }}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleValidateClient}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {refuseOpen && activeProforma && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-orange-400 flex items-center gap-2">
              <Ban className="h-5 w-5" /> Refus client — {activeProforma.numero}
            </h3>
            <textarea
              className="mt-4 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              rows={4}
              placeholder="Motif obligatoire…"
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRefuseOpen(false);
                  setActiveProforma(null);
                }}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRefuseClient}
                className="rounded-lg bg-orange-700 px-4 py-2 text-sm font-medium"
              >
                Enregistrer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
